import { createRouter, createWebHistory } from 'vue-router'
import { useCampaignStore, useReceiptStore } from '@/stores'
import { auth } from '@/auth'

// Lazy load components for better code splitting
const Home = () => import('@/pages/HomePage.vue')
const CampaignOver = () => import('@/pages/CampaignOverPage.vue')
const Error = () => import('@/pages/ErrorPage.vue')
const Expired = () => import('@/pages/ExpiredPage.vue')
const FAQ = () => import('@/pages/FAQPage.vue')
const IBAN = () => import('@/pages/IbanPage.vue')
const ProductUpload = () => import('@/pages/ProductUploadPage.vue')
const ReceiptUpload = () => import('@/pages/ReceiptUploadPage.vue')
const CampaignPage = () => import('@/pages/CampaignPage.vue')
const ReceiptReupload = () => import('@/pages/ReceiptReuploadPage.vue')
const Retailers = () => import('@/pages/RetailersPage.vue')
const ReuploadThankYou = () => import('@/pages/ReuploadThankYou.vue')
const Terms = () => import('@/pages/TermsPage.vue')
const ThankYou = () => import('@/pages/ThankYouPage.vue')
const IBANReupload = () => import('@/pages/IbanReuploadPage.vue')
const IBANThankYou = () => import('@/pages/IbanThankYou.vue')
const LoginCallback = () => import('@/pages/LoginCallback.vue')
const Winners = () => import('@/pages/WinnersPage.vue')
const Profile = () => import('@/pages/ProfilePage.vue')

const routes = [
  {
    path: '/',
    name: 'Home',
    component: Home,
    isPublic: true
  },
  {
    path: '/login/callback',
    name: 'Callback',
    component: LoginCallback,
    isPublic: true
  },
  {
    path: '/:campaign',
    name: 'CampaignPage',
    component: CampaignPage,
    isPublic: true
  },
  {
    path: '/:campaign/receipt-upload',
    name: 'ReceiptUploadPage',
    component: ReceiptUpload,
    meta: { requiresAuth: true },
    isPublic: true
  },
  {
    path: '/:campaign/iban',
    name: 'IbanPage',
    component: IBAN,
    meta: { requiresAuth: true }
  },
  {
    path: '/:campaign/thank-you',
    name: 'ThankYouPage',
    component: ThankYou,
    isPublic: false
  },
  {
    path: '/:campaign/campaign-is-over',
    name: 'CampaignOverPage',
    component: CampaignOver,
    isPublic: true
  },
  {
    path: '/:campaign?/error-page',
    name: 'ErrorPage',
    component: Error,
    isPublic: true
  },
  {
    path: '/:campaign/expired-page',
    name: 'ExpiredPage',
    component: Expired,
    isPublic: true
  },
  {
    path: '/:campaign/faq',
    name: 'FAQPage',
    component: FAQ,
    isPublic: true
  },
  {
    path: '/:campaign/product-upload',
    name: 'ProductUploadPage',
    component: ProductUpload,
    meta: { requiresAuth: true }
  },
  {
    path: '/:campaign/product-reupload',
    name: 'ProductReUploadPage',
    component: ProductUpload,
    isPublic: true
  },
  {
    path: '/:campaign/receipt-reupload',
    name: 'ReceiptReuploadPage',
    component: ReceiptReupload,
    isPublic: true
  },
  {
    path: '/:campaign/iban-reupload',
    name: 'IBANReuploadPage',
    component: IBANReupload,
    isPublic: true
  },
  {
    path: '/:campaign/iban-thank-you',
    name: 'IBANThankYou',
    component: IBANThankYou,
    isPublic: false
  },
  {
    path: '/:campaign/Magazine',
    name: 'RetailersPage',
    component: Retailers,
    isPublic: true
  },
  {
    path: '/:campaign/reupload-thank-you',
    name: 'ReuploadThankYou',
    component: ReuploadThankYou,
    isPublic: false
  },
  {
    path: '/:campaign/terms',
    name: 'TermsPage',
    component: Terms,
    isPublic: true
  },
  {
    path: '/castigatori',
    name: 'WinnersPage',
    component: Winners,
    isPublic: true
  },
  {
    path: '/:campaign?/profile',
    name: 'ProfilePage',
    component: Profile,
    meta: { requiresAuth: true },
    isPublic: true
  },
]

const router = createRouter({
  history: createWebHistory(''),
  routes
})

router.beforeEach(async (to, from, next) => {
  if (to.matched.length === 0) return next({ name: 'ErrorPage', params: { campaign: undefined } })
  if (to.params.force) return next()
  
  // Check if route requires authentication
  const requiresAuth = to.matched.some(record => record.meta.requiresAuth)
  
  if (requiresAuth) {
    try {
      // Check if user is authenticated
      if (!auth.isAuthenticated) {
        console.log('User not authenticated, redirecting to campaign page')
        const { campaign } = to.params
        return next({ 
          name: 'CampaignPage', 
          params: { campaign },
          query: { authRequired: 'true' } // Flag to show auth prompt
        })
      }
    } catch (error) {
      console.error('Error checking authentication:', error)
      // If auth check fails, allow through but the page will handle the error
    }
  }
  
  const { campaign } = to.params;
  const campaignStore = useCampaignStore();
  const receiptStore = useReceiptStore();
  const allowedPages = routes.filter(route => route.isPublic).map(route => route.name)

  // Check if we have a customer token in receipt store
  if (!receiptStore.customerToken) {
    await receiptStore.refreshTempId(to.query)
  }
  
  if (campaign && campaign !== campaignStore.activeCampaign?.domain) {
    try {
      const resp = await campaignStore.fetchCampaignByDomain(campaign)
      if (!resp) return next({ name: 'ErrorPage', params: { campaign: undefined } })

      const tz = 'Europe/Bucharest';

      // Get current date/time in Romania
      const currentDate = new Date(new Intl.DateTimeFormat('en-US', {
        timeZone: tz,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      }).format(new Date()));
        
      // Get end of campaign day in Romania
      const endDate = new Date(new Date(resp.uploadEndDate).toLocaleString('en-US', { timeZone: tz }));
      endDate.setHours(23, 59, 59, 999);

      if (endDate < currentDate) campaignStore.campaignIsEnded = true

      if (endDate < currentDate && !allowedPages.includes(to.name)) {
        return next({ name: 'CampaignOverPage', params: { campaign } })
      } else if (!allowedPages.includes(to.name)) {
        return next({ name: 'CampaignPage', params: { campaign } })
      }
    } catch (error) {
      // Remove params and redirect to error page 
      return next({ name: 'ErrorPage', params: { campaign: undefined } })
    }
  }

  const page = routes.find(route => route.name === to.name)
  if (!page) next('/error-page')
  else next()
})

export default router
